import { Button } from '@heroui/react'
import { LocateFixed, Minus, Plus, RotateCw } from 'lucide-react'
import { useCallback, useRef, useState, Suspense, useEffect, useMemo } from 'react'
import { useTranslations, useLocale } from 'use-intl'
import { MobileBottomSheet, type MobileResultsSnapState } from './MobileBottomSheet'
import { MobileDiscoveryFilterSheet, type MobileDiscoveryFilterSection } from './MobileDiscoveryFilterSheet'
import { CategoryChips } from './CategoryChips'
import { MapComponent } from './MapComponent'
import { MapHeader } from './MapHeader'
import { Sidebar } from './Sidebar'
import { getRouteApi } from '@tanstack/react-router'
import { useInfiniteNearbyShops } from '~/features/discovery/hooks/use-nearby-shops'
import { useShopDetail } from '~/features/shop-detail/hooks/use-shop-detail'
import { mapShopToCardProps } from '~/features/search/lib/map-shop-to-card-props'
import { toast } from 'sonner'
import { ShopQuickView } from '../quick-view/ShopQuickView'
import {
  DEFAULT_CENTER,
  DEFAULT_RADIUS,
} from '../../lib/map-constants'
import {
  computeViewportRadius,
  isInJapan,
  parseLocation,
  shouldShowSearchButton,
} from '../../lib/geo-utils'
import {
  clearDiscoverySelection,
  countActiveDiscoveryFilters,
  resetDiscoveryFilters,
} from '../../lib/filter-state'
import { shouldRenderSearchAreaButton } from '../../lib/map-affordances'

const routeApi = getRouteApi('/{-$locale}/discovery')

function DiscoveryMapContent() {
  const t = useTranslations('discovery')
  const locale = useLocale()
  
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  // URL 中的坐标是「已确认搜索的中心点」
  const { lat, lng, radius, categoryId, shopId, q, budget, nonSmoking, hasPrivateRoom, kidsAllowed, favoritesOnly, snapshotId, onlyShowSelected } = search
  
  const isOnlyShowSelected = !!onlyShowSelected && !!shopId

  const { data: nearbyShopsData, isLoading: isNearbyLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteNearbyShops(locale, {
    lat,
    lng,
    radius,
    categoryId,
    query: q,
    budget,
    nonSmoking,
    hasPrivateRoom,
    kidsAllowed,
    favoritesOnly,
    snapshotId,
  }, {
    enabled: !isOnlyShowSelected
  })

  const { data: singleShopData, isLoading: isSingleLoading } = useShopDetail(
    shopId?.toString() || '',
    locale,
    {
      enabled: isOnlyShowSelected
    }
  )

  const isLoading = isOnlyShowSelected ? isSingleLoading : isNearbyLoading
  
  const shops = useMemo(() => {
    if (isOnlyShowSelected) {
      return singleShopData ? [singleShopData] : []
    }
    if (!nearbyShopsData) return []
    return nearbyShopsData.pages.flatMap((page: any) => page?.data || [])
  }, [nearbyShopsData, singleShopData, isOnlyShowSelected])
  
  const restaurantsWithLocation = useMemo(() => {
    return shops.map((s: any) => {
      const props = mapShopToCardProps(s, t)
      const parsed = parseLocation(s.location)
      const sLat = parsed?.lat ?? 35.68
      const sLng = parsed?.lng ?? 139.69
      return {
        ...s,
        ...props,
        rawDistance: s.distance || 0,
        location: { lat: sLat, lng: sLng }
      }
    })
  }, [shops, t])

  // 跟踪地图当前中心点（本地状态，不写入 URL）
  const pendingCenter = useRef<{ lat: number, lng: number }>({ lat, lng })
  const pendingRadius = useRef<number>(radius)
  const lastSearchState = useRef({ center: { lat, lng }, radius })
  
  const sidebarRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)

  // 是否显示「搜索此区域」按钮
  const [showSearchArea, setShowSearchArea] = useState(false)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const [mobileFilterInitialSection, setMobileFilterInitialSection] = useState<MobileDiscoveryFilterSection>('filters')
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
  const [mapZoom, setMapZoom] = useState<number | null>(null)
  const [mobileResultsSnapState, setMobileResultsSnapState] = useState<MobileResultsSnapState>('peek')
  const [isDesktopLayout, setIsDesktopLayout] = useState(false)
  const [mobilePreviewShopId, setMobilePreviewShopId] = useState<number | null>(null)

  const [visibleRestaurants, setVisibleRestaurants] = useState(restaurantsWithLocation)
  const activeFilterCount = countActiveDiscoveryFilters(search)
  const mobileResultsExpanded = mobileResultsSnapState === 'expanded'
  const activeShopId = mobilePreviewShopId ?? shopId ?? null
  const showSearchAreaButton = shouldRenderSearchAreaButton({
    showSearchArea,
    isOnlyShowSelected,
    isMobileFilterOpen,
    hasSelectedShop: !!activeShopId,
    isDesktopLayout,
    mobileResultsSnapState,
  })

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1024px)')
    const syncDesktopLayout = () => setIsDesktopLayout(desktopQuery.matches)

    syncDesktopLayout()
    desktopQuery.addEventListener('change', syncDesktopLayout)
    return () => desktopQuery.removeEventListener('change', syncDesktopLayout)
  }, [])

  useEffect(() => {
    if (shopId) {
      setMobilePreviewShopId(null)
    }
  }, [shopId])

  // Map Display filter: cap map pins to MAX_MAP_DISPLAY_DISTANCE (20km)
  const MAX_MAP_DISPLAY_DISTANCE = 20000
  const mapDisplayRestaurants = useMemo(() => {
    return restaurantsWithLocation.filter((r: any) => (r.rawDistance ?? 0) <= MAX_MAP_DISPLAY_DISTANCE)
  }, [restaurantsWithLocation])

  // FitBounds logic for global search
  const searchParamsKey = `${q}-${lat}-${lng}-${categoryId}`
  const [lastFitKey, setLastFitKey] = useState('')
  const [boundsToFit, setBoundsToFit] = useState<google.maps.LatLngBoundsLiteral | null>(null)

  useEffect(() => {
    if (mapDisplayRestaurants.length > 0 && lastFitKey !== searchParamsKey) {
      let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180
      mapDisplayRestaurants.forEach((s: any) => {
        if (s.location.lat < minLat) minLat = s.location.lat
        if (s.location.lat > maxLat) maxLat = s.location.lat
        if (s.location.lng < minLng) minLng = s.location.lng
        if (s.location.lng > maxLng) maxLng = s.location.lng
      })
      // Ensure we don't fit to a single point perfectly
      if (maxLat - minLat < 0.001) { maxLat += 0.001; minLat -= 0.001 }
      if (maxLng - minLng < 0.001) { maxLng += 0.001; minLng -= 0.001 }

      // 视野设定最大值：避免缩放过小（例如最大距离超过20km时）
      // 这里如果坐标跨度太大，可以限制在一个合理的范围内
      const latDiff = maxLat - minLat
      const lngDiff = maxLng - minLng
      if (latDiff > 0.5) {
        const centerLat = (maxLat + minLat) / 2
        minLat = centerLat - 0.25; maxLat = centerLat + 0.25
      }
      if (lngDiff > 0.5) {
        const centerLng = (maxLng + minLng) / 2
        minLng = centerLng - 0.25; maxLng = centerLng + 0.25
      }

      setBoundsToFit({ north: maxLat, south: minLat, east: maxLng, west: minLng })
      setLastFitKey(searchParamsKey)
    }
  }, [mapDisplayRestaurants, searchParamsKey, lastFitKey])

  // When new data is fetched from the API, update visible restaurants
  useEffect(() => {
    if (q) {
      setVisibleRestaurants(restaurantsWithLocation)
    } else {
      // For non-global search, wait for map bounds to filter or default to all if map isn't ready
      setVisibleRestaurants(restaurantsWithLocation)
    }
  }, [restaurantsWithLocation, q])
  
  // User explicit pan location
  const [panToLocation, setPanToLocation] = useState<{lat: number, lng: number} | null>(null)
  // User real location
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null)

  // Hover state synchronization
  const [hoveredShopId, setHoveredShopId] = useState<number | null>(null)

  const handleBoundsChange = useCallback((bounds: google.maps.LatLngBounds | null, mapCenter?: google.maps.LatLng | null, map?: google.maps.Map) => {
    if (!bounds) return
    
    // For global search, don't filter the sidebar by map bounds
    if (search.q) {
      setVisibleRestaurants(restaurantsWithLocation)
    } else {
      let filtered = restaurantsWithLocation.filter((r: any) =>
        bounds.contains(new google.maps.LatLng(r.location.lat, r.location.lng)),
      )
      
      // Fallback if empty but a shop is selected
      if (filtered.length === 0 && search.shopId) {
        filtered = restaurantsWithLocation
      }
      
      setVisibleRestaurants(filtered)
    }

    // 只记录地图中心点，不写入 URL（不触发 API 重查）
    if (mapCenter && map) {
      const newCenter = { lat: mapCenter.lat(), lng: mapCenter.lng() }
      const newRadius = computeViewportRadius(map)
      setMapZoom(map.getZoom() ?? null)
      
      pendingCenter.current = newCenter
      pendingRadius.current = newRadius
      
      // 判断是否偏移或缩放足够大，显示「搜索此区域」按钮
      if (shouldShowSearchButton(lastSearchState.current, { center: newCenter, radius: newRadius })) {
        setShowSearchArea(true)
      } else {
        setShowSearchArea(false)
      }
    }
  }, [restaurantsWithLocation, search.shopId])

  // 点击「搜索此区域」：将地图中心点写入 URL → 触发 API refetch
  const handleSearchArea = useCallback(() => {
    const center = pendingCenter.current
    const rad = pendingRadius.current
    
    if (!isInJapan(center.lat, center.lng)) {
      toast.info(t('outOfJapan'))
      pendingCenter.current = DEFAULT_CENTER
      navigate({ search: (prev) => ({
        ...prev,
        lat: DEFAULT_CENTER.lat,
        lng: DEFAULT_CENTER.lng,
        radius: rad,
        shopId: undefined,
        onlyShowSelected: undefined,
      }), replace: true })
      setShowSearchArea(false)
      return
    }

    lastSearchState.current = { center, radius: rad }
    setMobilePreviewShopId(null)
    
    navigate({ search: (prev) => ({
      ...prev,
      lat: center.lat,
      lng: center.lng,
      radius: rad,
      shopId: undefined, // 清除选中
      onlyShowSelected: undefined,
    })})
    setShowSearchArea(false)
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [navigate, t])

  const setSelectedCategory = (id: string) => {
    setMobilePreviewShopId(null)
    navigate({ 
      search: (prev) => ({
        ...clearDiscoverySelection(prev),
        categoryId: Number(id) || undefined,
      }),
      replace: true 
    })
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }
  
  const handleToggleFavorites = (val: boolean) => {
    setMobilePreviewShopId(null)
    navigate({
      search: (prev) => ({
        ...clearDiscoverySelection(prev),
        favoritesOnly: val ? true : undefined,
      }),
      replace: true
    })
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const setSelectedId = (id: number | null) => {
    setMobilePreviewShopId(null)
    navigate({
      search: (prev) => ({
        ...prev,
        shopId: id || undefined,
        onlyShowSelected: id ? prev.onlyShowSelected : undefined,
      }),
    })
  }

  const handleMarkerClick = (id: number) => {
    if (isDesktopLayout) {
      setSelectedId(id)
      return
    }

    setMobilePreviewShopId(id)
  }

  const handleResetFilters = () => {
    setMobilePreviewShopId(null)
    navigate({
      search: (prev) => resetDiscoveryFilters(prev),
      replace: true,
    })
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const openMobileFilters = (section: MobileDiscoveryFilterSection) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setMobileFilterInitialSection(section)
    setIsMobileFilterOpen(true)
  }

  const handleZoom = (delta: number) => {
    if (!mapInstance) return
    const currentZoom = mapInstance.getZoom() ?? 13
    const nextZoom = currentZoom + delta
    mapInstance.setZoom(nextZoom)
    setMapZoom(nextZoom)
  }

  // 组件挂载或参数变更时校验 URL 坐标
  useEffect(() => {
    if (!isInJapan(lat, lng)) {
      toast.info(t('outOfJapan'))
      pendingCenter.current = DEFAULT_CENTER
      navigate({ search: (prev) => ({
        ...prev,
        lat: DEFAULT_CENTER.lat,
        lng: DEFAULT_CENTER.lng,
      }), replace: true })
    }
  }, [lat, lng, navigate, t])

  // 组件挂载时自动进行一次地理位置探测
  useEffect(() => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        if (!isInJapan(loc.lat, loc.lng)) {
          toast.info(t('outOfJapan'))
          return
        }
        setUserLocation(loc)
        setPanToLocation(loc)
        lastSearchState.current = { center: loc, radius: DEFAULT_RADIUS }
        navigate({ search: (prev) => ({ ...prev, lat: loc.lat, lng: loc.lng, radius: DEFAULT_RADIUS }) })
      },
      () => {
        toast.info(t('locationFallback', { defaultValue: 'Unable to get your location. Showing popular area in Tokyo.' }))
      },
      { timeout: 8000, maximumAge: 300_000 }
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude }
          if (!isInJapan(loc.lat, loc.lng)) {
            toast.info(t('outOfJapan'))
            return
          }
          setUserLocation(loc)
          setPanToLocation(loc)
          lastSearchState.current = { center: loc, radius: DEFAULT_RADIUS }
          setMobilePreviewShopId(null)
          navigate({ search: (prev) => ({ ...prev, lat: loc.lat, lng: loc.lng, radius: DEFAULT_RADIUS, shopId: undefined, onlyShowSelected: undefined }) })
          setShowSearchArea(false)
        },
        (error) => {
          console.error('Error getting location:', error)
        }
      )
    }
  }

  const handleSelectArea = useCallback((newLat: number, newLng: number) => {
    setPanToLocation({ lat: newLat, lng: newLng })
    lastSearchState.current = { center: { lat: newLat, lng: newLng }, radius: DEFAULT_RADIUS }
    navigate({
      search: (prev) => ({
        ...prev,
        lat: newLat,
        lng: newLng,
        radius: DEFAULT_RADIUS,
        shopId: undefined,
        onlyShowSelected: undefined,
      }),
    })
    setMobilePreviewShopId(null)
    setShowSearchArea(false)
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [navigate])

  return (
    <div className="bg-stone-50 dark:bg-stone-900 text-stone-900 dark:text-stone-100 w-full h-screen overflow-hidden flex flex-col relative selection:bg-primary selection:text-white">

      {/* Global Header (Fixed at top) */}
      <MapHeader
        onBack={() => {}}
        onSearch={() => {}}
        onSearchClick={() => openMobileFilters('search')}
        onFilterClick={() => openMobileFilters('filters')}
        activeFilterCount={activeFilterCount}
      />

      <div className="flex flex-1 overflow-hidden pt-20 w-full">

        {/* Desktop Sidebar & Mobile List View */}
        {isDesktopLayout && (
          <Sidebar
            scrollRef={sidebarRef}
            selectedCategory={String(categoryId || '')}
            onSelectCategory={setSelectedCategory}
            favoritesOnly={favoritesOnly}
            onToggleFavorites={handleToggleFavorites}
            restaurants={visibleRestaurants}
            selectedId={shopId || null}
            onSelectRestaurant={setSelectedId}
            onHover={setHoveredShopId}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            className="hidden lg:flex lg:relative lg:w-[40%] lg:pt-8 lg:z-10"
            lat={lat}
            lng={lng}
            onSelectArea={handleSelectArea}
          />
        )}

        {/* Map Area (Mobile 100%, Desktop 60%) */}
        <main className="flex lg:flex flex-1 relative w-full h-full bg-stone-200 dark:bg-stone-800">
          <MapComponent
            restaurants={mapDisplayRestaurants}
            selectedId={activeShopId}
            hoveredId={hoveredShopId}
            userLocation={userLocation}
            onBoundsChange={handleBoundsChange}
            onMarkerClick={handleMarkerClick}
            initialCenter={{ lat, lng }}
            panToLocation={panToLocation}
            boundsToFit={boundsToFit}
            onMapReady={setMapInstance}
          />

          {/* Map Controls */}
          <div
            className={`absolute right-4 bottom-[56%] lg:bottom-auto lg:top-48 flex-col gap-2 z-50 ${mobileResultsExpanded ? 'hidden lg:flex' : 'flex'}`}
            data-map-zoom={mapZoom ?? undefined}
            data-testid="map-controls"
          >
            <Button
              isIconOnly
              aria-label={t('locateMe', { defaultValue: 'Locate me' })}
              onPress={handleLocateMe}
              className="h-11 min-w-11 bg-white/90 dark:bg-stone-800/90 backdrop-blur-md rounded-xl shadow-[0_8px_16px_rgba(0,0,0,0.06)] text-stone-700 dark:text-stone-300"
              data-testid="map-locate-button"
            >
              <LocateFixed className="w-6 h-6" />
            </Button>
            <div className="flex flex-col bg-white/90 dark:bg-stone-800/90 backdrop-blur-md rounded-xl shadow-[0_8px_16px_rgba(0,0,0,0.06)] overflow-hidden mt-2">
              <Button
                isIconOnly
                aria-label={t('zoomIn', { defaultValue: 'Zoom in' })}
                variant="ghost"
                isDisabled={!mapInstance}
                onPress={() => handleZoom(1)}
                className="h-11 min-w-11 rounded-none text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                data-testid="map-zoom-in"
              >
                <Plus className="w-6 h-6" />
              </Button>
              <div className="w-full h-[1px] bg-stone-200 dark:bg-stone-700"></div>
              <Button
                isIconOnly
                aria-label={t('zoomOut', { defaultValue: 'Zoom out' })}
                variant="ghost"
                isDisabled={!mapInstance}
                onPress={() => handleZoom(-1)}
                className="h-11 min-w-11 rounded-none text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                data-testid="map-zoom-out"
              >
                <Minus className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Search Area Button — 仅在拖动地图后显示 */}
          {showSearchAreaButton && (
            <div className="absolute top-24 lg:top-8 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              <Button
                onPress={handleSearchArea}
                className="min-h-11 rounded-full px-5 py-2 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.08)] text-sm font-medium text-stone-900 dark:text-stone-100"
              >
                <RotateCw className="w-[18px] h-[18px] text-primary mr-1" />
                {t('searchArea')}
              </Button>
            </div>
          )}

          {/* Empty state overlay when no shops in viewport */}
          {visibleRestaurants.length === 0 && !showSearchArea && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg max-w-xs text-center pointer-events-auto">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  {t('noResultsInArea', { defaultValue: 'No restaurants found in this area. Try zooming out or removing filters.' })}
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {activeFilterCount > 0 && (
                    <Button
                      size="sm"
                      variant="primary"
                      className="min-h-11 rounded-full px-4"
                      onPress={handleResetFilters}
                      data-testid="empty-state-reset-filters"
                    >
                      {t('reset', { defaultValue: 'Reset' })}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={activeFilterCount > 0 ? 'outline' : 'primary'}
                    className="min-h-11 rounded-full px-4"
                    onPress={() => handleZoom(-1)}
                    data-testid="empty-state-zoom-out"
                  >
                    {t('zoomOut', { defaultValue: 'Zoom out' })}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Category Chips (Hidden on Desktop) */}
          {!mobileResultsExpanded && (
            <div className="lg:hidden absolute top-0 left-0 w-full z-40 pointer-events-none pt-[env(safe-area-inset-top)]" data-testid="mobile-category-chips">
              <div className="pointer-events-auto">
                <CategoryChips
                  selectedId={String(categoryId || '')}
                  onSelect={setSelectedCategory}
                  favoritesOnly={favoritesOnly}
                  onToggleFavorites={handleToggleFavorites}
                />
              </div>
            </div>
          )}
        </main>

        {/* Mobile Bottom Sheet (Hidden on Desktop) */}
        <div className="lg:hidden">
          <MobileBottomSheet
            scrollRef={drawerRef}
            restaurants={visibleRestaurants}
            selectedId={activeShopId}
            onSelectRestaurant={setSelectedId}
            onHover={setHoveredShopId}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            lat={lat}
            lng={lng}
            onSelectArea={handleSelectArea}
            onSnapStateChange={setMobileResultsSnapState}
            isObscured={isMobileFilterOpen || !!shopId}
          />
        </div>
      </div>

      {/* Shop Quick View */}
      {shopId && (
        <Suspense fallback={null}>
          <ShopQuickView 
            shopId={shopId} 
            locale={locale} 
            onClose={() => setSelectedId(null)} 
          />
        </Suspense>
      )}

      <MobileDiscoveryFilterSheet
        isOpen={isMobileFilterOpen}
        initialSection={mobileFilterInitialSection}
        activeFilterCount={activeFilterCount}
        selectedCategory={String(categoryId || '')}
        favoritesOnly={favoritesOnly}
        onClose={() => setIsMobileFilterOpen(false)}
        onReset={handleResetFilters}
        onSelectCategory={setSelectedCategory}
        onToggleFavorites={handleToggleFavorites}
      />
    </div>
  )
}

export default function DiscoveryMapPage() {
  return (
    <Suspense fallback={<div className="w-full h-screen bg-stone-50" />}>
      <DiscoveryMapContent />
    </Suspense>
  )
}
